import { VideoType } from "../services/api/video"
import { Box, CardMedia, styled, Tooltip, Typography, useTheme } from "@mui/material"
import React, { useEffect, useState } from "react"
import { FormatDate, StylelessLink } from "./Util"
import DefaultThumbnail from "../assets/default-thumbnail.jpg"
import AspectRatioBox from "./AspectRatioBox"
import { useAuthContext } from "../services/auth"
import { HoverOverlay, Overlay } from "./StyledElements"
import { useStaticContext } from "../services/static"
import { VideoContextMenu } from "./VideoContextMenu"
import { UserRole } from "../services/auth/auth.types"
import useWindowDimensions from "../services/useWindowDimensions"

import { ReactComponent as FlameIcon } from "../assets/svg/flame.svg"
import { ReactComponent as LockIcon } from "../assets/svg/lock.svg"
import { ReactComponent as KeyIcon } from "../assets/svg/key.svg"
import { ReactComponent as InternetIcon } from "../assets/svg/internet.svg"

const VideoCardMedia = styled(CardMedia)({
  paddingTop: "56.25%",
})

interface VideoThumbnailProps {
  video: VideoType
  hideTrendingIcon?: boolean
  playlistCode?: string
}

export const VideoThumbnail = ({
  video,
  hideTrendingIcon = false,
  playlistCode,
}: VideoThumbnailProps): React.ReactElement => {
  const { isAuth, role } = useAuthContext()
  const { trendingVideos } = useStaticContext()
  const isTrending = trendingVideos.some((v) => v.id === video.id)
  const { width } = useWindowDimensions()
  const theme = useTheme()
  const [contextMenu, setContextMenu] = React.useState<{
    mouseX: number
    mouseY: number
  } | null>(null)

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault()

    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    )
  }

  const handleClose = () => {
    setContextMenu(null)
  }

  return (
    <Box mb={2} onContextMenu={handleContextMenu}>
      <StylelessLink to={"/video?v=" + video.id + (playlistCode ? "&pl=" + playlistCode : "")}>
        <HoverOverlay sx={{ display: "flex", flexDirection: "column", height: "fit-content" }}>
          <AspectRatioBox ratio={16 / 9}>
            <VideoThumbnailImage video={video} />
            <Overlay className="overlay" />
          </AspectRatioBox>
          <Box mt={1} sx={{ position: "relative", zIndex: 2 }}>
            <Box display="flex" alignItems="center" minWidth={0}>
              <Typography noWrap variant="h5" fontSize={"1.2rem"} textOverflow="ellipsis" sx={{ minWidth: 0 }}>
                {video.title}
              </Typography>
              {(((role === UserRole.HYRIS || role === UserRole.GEEK) && video.privacy !== 1) ||
                (isTrending && !hideTrendingIcon)) && (
                <Box ml={"4px"} component={"span"} display="inline-flex" alignItems="center" flexShrink={0}>
                  {isTrending && !hideTrendingIcon && (
                    <Tooltip title={"A la une"}>
                      <FlameIcon width={"auto"} height={"1rem"} />
                    </Tooltip>
                  )}
                  {(role === UserRole.HYRIS || role === UserRole.GEEK) &&
                    (video.privacy === 0 ? (
                      <Tooltip title={"Vidéo publique"}>
                        <InternetIcon width={"1rem"} height={"1rem"} fill={theme.palette.soft.main} />
                      </Tooltip>
                    ) : video.privacy === 2 ? (
                      <Tooltip title={video.gpaRestricted ? "Cachée aux GPA" : "Vidéo privée"}>
                        <KeyIcon width={"1rem"} height={"1rem"} fill={theme.palette.soft.main} />
                      </Tooltip>
                    ) : (
                      video.privacy === 3 && (
                        <Tooltip title={"Vidéo archivée"}>
                          <LockIcon width={"1rem"} height={"1rem"} fill={theme.palette.soft.main} />
                        </Tooltip>
                      )
                    ))}
                </Box>
              )}
            </Box>
            <Typography noWrap variant="subtitle1" fontSize={".8rem"} color={"gray"} textOverflow="ellipsis">
              {FormatDate(video.date, width < theme.breakpoints.values.lg) +
                (video.promotion && video.promotion >= 0 ? ` - P${video.promotion}` : "") +
                " - " +
                video.views +
                " vue" +
                (video.views > 1 ? "s" : "") +
                (video.likeCount && isAuth()
                  ? " - " + video.likeCount + " like" + (video.likeCount > 1 ? "s" : "")
                  : "")}
            </Typography>
          </Box>
        </HoverOverlay>
      </StylelessLink>
      <VideoContextMenu
        video={video}
        open={contextMenu !== null}
        onClose={handleClose}
        position={contextMenu ? { x: contextMenu.mouseX, y: contextMenu.mouseY } : null}
      />
    </Box>
  )
}

export const VideoThumbnailImage = ({
  video,
  adaptiveBorder = true,
  borderRadius = "10px",
  opacity = 1,
}: {
  video: VideoType | null
  adaptiveBorder?: boolean
  borderRadius?: string
  opacity?: number
}): React.ReactElement => {
  const [thumbnailSource, setThumbnailSource] = useState<string | null>(null)

  useEffect(() => {
    if (!video) return
    const fetchThumbnail = async () => {
      try {
        const response = await fetch(`/api/media/thumbnail/${video.id}`, {
          credentials: "same-origin",
        })

        if (response.status === 200) {
          const blob = await response.blob()
          const thumbnailURL = URL.createObjectURL(blob)
          setThumbnailSource(thumbnailURL)
        } else {
          setThumbnailSource(null)
        }
      } catch (error) {
        console.error("Error fetching thumbnail:", error)
        setThumbnailSource(null)
      }
    }

    fetchThumbnail()
  }, [video])

  return video && thumbnailSource ? (
    <VideoCardMedia
      image={thumbnailSource || undefined}
      title={video.title}
      className={adaptiveBorder ? "adaptiveBorderRadius" : ""}
      sx={{ borderRadius: adaptiveBorder ? "unset" : borderRadius, opacity: opacity, width: "100%", height: "100%" }}
    />
  ) : (
    <img
      src={DefaultThumbnail}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        borderRadius: adaptiveBorder ? "unset" : borderRadius,
        opacity: opacity,
      }}
      className={adaptiveBorder ? "adaptiveBorderRadius" : ""}
    />
  )
}
